import { Lock } from "lucide-react";
import WizardShell from "./WizardShell";
import VoiceTextarea from "@/components/VoiceTextarea";
import ContextPanel from "@/components/ContextPanel";

const REFLECT_QUESTIONS = [
  {
    id: "assumptions",
    label: "What are you assuming here?",
    placeholder: "The quiet beliefs you haven't tested yet…",
    rows: 3,
  },
  {
    id: "evidence",
    label: "What evidence do you have?",
    placeholder: "Conversations, signal, data — be honest about how strong it is.",
    rows: 3,
  },
  {
    id: "might_be_missing",
    label: "What might you be missing?",
    placeholder: "A perspective, a number, a person you haven't spoken to.",
    rows: 3,
  },
];

export default function AttentionStep({
  value,
  onChange,
  firstName,
  onFirstNameChange,
  contextText,
  contextLabel,
  onSetContext,
  onClearContext,
  onNext,
}) {
  const set = (k, v) => onChange({ ...value, [k]: v });

  const canContinue = value.working_on.trim().length > 5;

  return (
    <WizardShell
      pillLabel="Step 1 · Attention"
      pillColor="#4aa0a4"
      headline="What are you currently working on?"
      aiOpening={
        "Take a breath. Describe it the way you'd describe it to a friend who is paying real attention.\n\nThen sit with three questions before you move on."
      }
      testid="attention-step"
      footer={
        <>
          <span />
          <button
            type="button"
            className="weave-btn-primary"
            onClick={onNext}
            disabled={!canContinue}
            data-testid="attention-next-btn"
          >
            Continue to Witness
          </button>
        </>
      }
    >
      <ContextPanel
        context={contextText}
        sourceLabel={contextLabel}
        onSet={onSetContext}
        onClear={onClearContext}
      />

      <div className="weave-name-field" data-testid="first-name-field">
        <label
          className="block text-sm text-slate-500"
          htmlFor="attention-first-name"
        >
          What should I call you?{" "}
          <span className="text-slate-400">(optional)</span>
        </label>
        <input
          id="attention-first-name"
          type="text"
          autoComplete="off"
          placeholder="Just a first name is fine — used only on this device."
          value={firstName || ""}
          onChange={(e) => onFirstNameChange(e.target.value)}
          maxLength={40}
          data-testid="first-name-input"
        />
        <span className="weave-name-privacy">
          <Lock size={12} aria-hidden="true" />
          Your name stays in your browser. Nothing is sent or stored.
        </span>
      </div>

      <div>
        <label
          className="block text-sm text-slate-500 mb-2"
          htmlFor="attention-working-on"
        >
          The idea, project, or problem you're holding right now
        </label>
        <VoiceTextarea
          rows={4}
          placeholder="I'm working on…"
          value={value.working_on}
          onChange={(v) => set("working_on", v)}
          ariaLabel="The idea, project or problem you are holding right now"
          testid="attention-working-on-input"
          micTestid="attention-working-on-mic"
        />
      </div>

      <div className="weave-divider" />

      <p className="text-sm uppercase tracking-[0.18em] text-slate-400 mb-4">
        Three reflective questions
      </p>

      <div className="space-y-6">
        {REFLECT_QUESTIONS.map((q) => (
          <div key={q.id}>
            <label className="block text-base text-slate-700 mb-2">
              {q.label}
            </label>
            <VoiceTextarea
              rows={q.rows}
              placeholder={q.placeholder}
              value={value[q.id]}
              onChange={(v) => set(q.id, v)}
              ariaLabel={q.label}
              testid={`attention-${q.id}-input`}
              micTestid={`attention-${q.id}-mic`}
            />
          </div>
        ))}
      </div>
    </WizardShell>
  );
}
